<script setup>
import { computed, toRefs } from "vue";

import SvgIcon from 'JIX/components/SvgIcon.vue';
import { __ } from 'JIX/gettext.js';

import 'icons/inline-pagination-left.svg';
import 'icons/inline-pagination-right.svg';

const props = defineProps({
    items: {
        type: Number,
        required: true,
    },
    baseUrl: {
        type: [String, URL],
        required: false,
        default: () => null,
    },
    pageParam: {
        type: String,
        required: false,
        default: () => 'page',
    },
    perPage: {
        type: Number,
        required: false,
        default: () => 20,
    },
    page: {
        type: Number,
        required: false,
        default: () => 1,
    },
    numPageLinks: {
        type: Number,
        required: false,
        default: () => 2,
    },
    hideLastPage: {
        type: Boolean,
        required: false,
        default: () => false,
    },
});
defineEmits(['update:page']);

const { baseUrl, items, perPage, page, pageParam, hideLastPage, numPageLinks } = toRefs(props);

const numPages = computed(() => 1 + Math.floor((items.value - 1) / perPage.value));
const virtualPage = computed(() => Math.max(1, Math.min(page.value, numPages.value)));
const pagesRange = computed(() => {
    const minPage = 2;
    const maxPage = hideLastPage.value ? numPages.value : numPages.value - 1;

    let start = virtualPage.value - numPageLinks.value;
    let end   = virtualPage.value + numPageLinks.value;

    // Increase the range in the opposite end if near one of the ends
    if (start < minPage) {
        end += minPage - start;
    } else if (end > maxPage) {
        start -= end - maxPage;
    }

    // If preceding/succeeding items are not left out increase the range in the
    // other end to compensate for the missing "..."
    if (start <= minPage) { end++; }
    if (end   >= maxPage) { start--; }

    // Don't skip a single number
    if (start === minPage + 1) { start--; }
    if (end   === maxPage - 1) { end++; }

    // Make sure we stay within the bounds
    start = Math.max(start, minPage);
    end = Math.min(end, maxPage);

    return [start, end];
});
const pagesRangeList = computed(() => {
    const [start, end] = pagesRange.value;
    let pages = [];
    for (let i = start; i <= end; i++) { pages.push(i); }
    return pages;
});
const precedingLeftOut = computed(() => {
    const [start, _end] = pagesRange.value;
    return start > 2;
});
const succeedingLeftOut = computed(() => {
    const [_start, end] = pagesRange.value;
    return end < numPages.value - 1;
});
const nextPage = computed(() => virtualPage.value === numPages.value ? null : virtualPage.value + 1);
const prevPage = computed(() => virtualPage.value === 1 ? null : virtualPage.value - 1);

function getPageUrl(pageNumber) {
    if (!baseUrl.value) {
        return '#';
    }
    const url = new URL(baseUrl.value);
    if (pageNumber === 1) {
        url.searchParams.delete(pageParam.value);
    } else {
        url.searchParams.set(pageParam.value, pageNumber);
    }
    return url;
}

function pageListClasses(page) {
    const res = { 'page-link': true };
    res['rounded-left'] = page === 1;
    res['rounded-0'] = page > 1 || page < numPages.value;
    res['rounded-right'] = page === numPages.value;
    return res;
}
</script>

<template>
    <nav>
        <ul class="pagination" v-if="numPages > 1 || page > numPages">
            <li class="page-item page-item-prev" v-if="prevPage !== null">
                <a :href="getPageUrl(prevPage)" accesskey="f" @click.prevent="$emit('update:page', prevPage)">
                    <SvgIcon name="inline-pagination-left" size="mini" :aria-label="__('Forrige')" />
                </a>
            </li>
            <li :class="['page-item', {'active': virtualPage == 1}]">
                <a :href="getPageUrl(1)" :class="pageListClasses(1)" @click.prevent="$emit('update:page', 1)">{{ 1 }}</a>
            </li>
            <li class="page-item page-item-gap" v-if="precedingLeftOut">
                &hellip;
            </li>
            <li
                :class="['page-item', {'active': i === virtualPage}]"
                v-for="i in pagesRangeList"
                :key="i"
            >
                <a
                    :href="getPageUrl(i)"
                    class="page-link"
                    :class="pageListClasses(i)"
                    @click.prevent="$emit('update:page', i)"
                >{{ i }}</a>
            </li>
            <li class="page-item page-item-gap" v-if="succeedingLeftOut">
                &hellip;
            </li>
            <li
                :class="['page-item', {'active': numPages === virtualPage}]"
                v-if="!hideLastPage && numPages > 1"
            >
                <a :href="getPageUrl(numPages)" :class="pageListClasses(numPages)" @click.prevent="$emit('update:page', numPages)">{{ numPages }}</a>
            </li>
            <li class="page-item page-item-next" v-if="nextPage !== null">
                <a :href="getPageUrl(nextPage)" accesskey="n" @click.prevent="$emit('update:page', nextPage)">
                    <SvgIcon name="inline-pagination-right" size="mini" :aria-label="__('Næste')" />
                </a>
            </li>
        </ul>
    </nav>
</template>
